import type { Part } from './catalog';

/*
 * Parts data in XML: a small, dependency-free XML reader and converters for
 * the formats manufacturers and distributors hand out, turned into the same
 * rows (header + data) the Excel/CSV import reads:
 *  - BMEcat 1.2 / 2005 (ARTICLE / PRODUCT), with ETIM classes and prices
 *  - EPLAN parts management XML (<part P_ARTICLE_… />, multilingual texts)
 *  - any other XML with one repeated record element (fields as attributes or children)
 */

export interface XmlNode {
  name: string;
  attrs: Record<string, string>;
  children: XmlNode[];
  text: string;
}

const ENT: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };
const decode = (s: string) =>
  s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (all, e: string) =>
    e[0] === '#' ? String.fromCodePoint(e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10)) : ENT[e] ?? all,
  );

/** Parses XML into a tree (namespaces are dropped from names). Throws on badly broken input. */
export function parseXml(src: string): XmlNode {
  const root: XmlNode = { name: '#root', attrs: {}, children: [], text: '' };
  const stack: XmlNode[] = [root];
  const local = (n: string) => n.slice(n.indexOf(':') + 1);
  let i = 0;
  const n = src.length;
  while (i < n) {
    const lt = src.indexOf('<', i);
    const textEnd = lt < 0 ? n : lt;
    if (textEnd > i) {
      const t = src.slice(i, textEnd);
      if (t.trim()) stack[stack.length - 1].text += decode(t);
    }
    if (lt < 0) break;
    if (src.startsWith('<!--', lt)) {
      const e = src.indexOf('-->', lt + 4);
      i = e < 0 ? n : e + 3;
    } else if (src.startsWith('<![CDATA[', lt)) {
      const e = src.indexOf(']]>', lt + 9);
      stack[stack.length - 1].text += src.slice(lt + 9, e < 0 ? n : e);
      i = e < 0 ? n : e + 3;
    } else if (src[lt + 1] === '?' || src[lt + 1] === '!') {
      // declaration / doctype (with a possible internal subset)
      let e = src.indexOf('>', lt);
      const br = src.indexOf('[', lt);
      if (src[lt + 1] === '!' && br >= 0 && br < e) e = src.indexOf(']>', br) + 1;
      i = e < 0 ? n : e + 1;
    } else if (src[lt + 1] === '/') {
      const e = src.indexOf('>', lt);
      const name = local(src.slice(lt + 2, e).trim());
      // close up to the matching element (tolerates small nesting slips)
      for (let k = stack.length - 1; k > 0; k--)
        if (stack[k].name === name) {
          stack.length = k;
          break;
        }
      i = e + 1;
    } else {
      // start tag: find its end outside quoted attribute values
      let e = lt + 1, q = '';
      for (; e < n; e++) {
        const c = src[e];
        if (q) {
          if (c === q) q = '';
        } else if (c === '"' || c === "'") q = c;
        else if (c === '>') break;
      }
      const raw = src.slice(lt + 1, e);
      const self = raw.endsWith('/');
      const body = self ? raw.slice(0, -1) : raw;
      const m = /^([^\s/>]+)/.exec(body);
      if (!m) throw new Error('XML okunamadı.');
      const node: XmlNode = { name: local(m[1]), attrs: {}, children: [], text: '' };
      for (const a of body.slice(m[1].length).matchAll(/([^\s=]+)\s*=\s*("([^"]*)"|'([^']*)')/g)) node.attrs[local(a[1])] = decode(a[3] ?? a[4] ?? '');
      stack[stack.length - 1].children.push(node);
      if (!self) stack.push(node);
      i = e + 1;
    }
  }
  if (!root.children.length) throw new Error('XML okunamadı.');
  return root.children[0];
}

const all = (n: XmlNode, name: string, out: XmlNode[] = []): XmlNode[] => {
  for (const c of n.children) {
    if (c.name === name) out.push(c);
    else all(c, name, out);
  }
  return out;
};
const first = (n: XmlNode | undefined, ...path: string[]): XmlNode | undefined => {
  let cur = n;
  for (const p of path) cur = cur && (cur.children.find((c) => c.name === p) ?? all(cur, p)[0]);
  return cur;
};
const txt = (n: XmlNode | undefined) => (n?.text ?? '').trim();

/** ISO 639-2 (BMEcat) and locale codes (EPLAN) → interface languages. */
const LANG3: Record<string, string> = {
  tur: 'tr', eng: 'en', deu: 'de', ger: 'de', fra: 'fr', fre: 'fr', spa: 'es', por: 'pt', rus: 'ru', ara: 'ar', zho: 'zh', chi: 'zh', hin: 'hi', jpn: 'ja',
};
const langOf = (code: string) => {
  const c = code.toLowerCase();
  return LANG3[c] ?? (/^[a-z]{2}([_-]|$)/.test(c) ? c.slice(0, 2) : '');
};
const OTHER = ['en', 'de', 'es', 'fr', 'pt', 'ru', 'ar', 'zh', 'hi', 'ja'];

/** ETIM classes with a clear symbol type (others fall back to the words in the description). */
const ETIM: Record<string, string> = {
  EC000042: 'cb1', // miniature circuit breaker
  EC000066: 'coil', // power contactor, AC switching
  EC000074: 'cb3', // motor protective circuit-breaker
  EC000106: 'ol3', // thermal overload relay
  EC000897: 'term', // feed-through terminal block
};

export interface XmlRows {
  format: 'bmecat' | 'eplan' | 'generic';
  rows: string[][];
}

/** EnerCAD's own part fields, written as features of this feature system and read back from it. */
// (the schema's pattern does not take the "udf_" prefix the specification suggests for user-defined systems)
const FEATURE_SYSTEM = 'ENERCAD-1.0';
const FEATURES = [
  ['NO', 'NO'], ['NC', 'NC'], ['Poles', 'Kutup'], ['Channels', 'Kanal'], ['Weight', 'Ağırlık (kg)'], ['CableType', 'Kablo tipi'], ['Cores', 'Damarlar'],
  ['CrossSection', 'Kesit'], ['Shield', 'Ekran'], ['AuxContacts', 'Yardımcı kontaklar'], ['Visual', 'Görsel kaynağı'],
] as const;
const HEAD = ['Parça no', 'Üretici', 'Tanım', ...OTHER.map((l) => `Tanım ${l.toUpperCase()}`), 'Sembol tipi', 'Fiyat', 'Para birimi', 'Genişlik', 'Yükseklik', 'Derinlik', 'Bağlantı', ...FEATURES.map(([, h]) => h), 'Aksesuarlar'];
type Rec = { pn: string; maker?: string; d: Record<string, string>; type?: string; price?: string; cur?: string; w?: string; h?: string; dp?: string; url?: string; f?: Record<string, string>; acc?: string };
const toRow = (r: Rec) => {
  const tr = r.d.tr || r.d.en || Object.values(r.d)[0] || '';
  return [r.pn, r.maker ?? '', tr, ...OTHER.map((l) => (r.d[l] && r.d[l] !== tr ? r.d[l] : '')), r.type ?? '', r.price ?? '', r.cur ?? '', r.w ?? '', r.h ?? '', r.dp ?? '', r.url ?? '', ...FEATURES.map(([k]) => r.f?.[k] ?? ''), r.acc ?? ''];
};

interface BmecatCtx {
  lang: string;
  cur: string;
}
const bmecatCtx = (header: XmlNode | undefined): BmecatCtx => ({
  lang: langOf(txt(first(header, 'LANGUAGE')) || 'deu') || 'de',
  cur: txt(first(header, 'CURRENCY')),
});

function bmecat(doc: XmlNode): string[][] {
  const ctx = bmecatCtx(first(doc, 'HEADER'));
  const items = [...all(doc, 'PRODUCT'), ...all(doc, 'ARTICLE')];
  return items.map((p) => bmecatRec(p, ctx)).filter((r) => r.pn).map(toRow);
}

function bmecatRec(p: XmlNode, ctx: BmecatCtx): Rec {
  const defLang = ctx.lang, defCur = ctx.cur;
  {
    const d: Record<string, string> = {};
    for (const n of [...all(p, 'DESCRIPTION_SHORT')]) {
      const l = langOf(n.attrs.lang ?? '') || defLang;
      if (txt(n) && !d[l]) d[l] = txt(n);
    }
    // a short description cut with "…" (BMEcat allows 150 characters): the whole text is the long one
    for (const n of all(p, 'DESCRIPTION_LONG')) {
      const l = langOf(n.attrs.lang ?? '') || defLang;
      if (d[l]?.endsWith('…') && txt(n).startsWith(d[l].slice(0, -1))) d[l] = txt(n);
    }
    // EnerCAD's own features (a file written by EnerCAD): symbol types, contacts, sizes, cable data …
    const own = all(p, 'PRODUCT_FEATURES').filter((g) => txt(first(g, 'REFERENCE_FEATURE_SYSTEM_NAME')) === FEATURE_SYSTEM);
    const f: Record<string, string> = {};
    for (const n of own.flatMap((g) => all(g, 'FEATURE'))) f[txt(first(n, 'FNAME'))] = all(n, 'FVALUE').map(txt).join(', ');
    const acc = all(p, 'PRODUCT_REFERENCE')
      .filter((r) => r.attrs.type === 'accessories' && txt(first(r, 'PROD_ID_TO')))
      .map((r) => txt(first(r, 'PROD_ID_TO')) + (Number(r.attrs.quantity) > 1 ? `×${r.attrs.quantity}` : ''))
      .join(', ');
    const priceNode = all(p, 'PRODUCT_PRICE').concat(all(p, 'ARTICLE_PRICE'));
    const price = priceNode.find((x) => /net_list|net_customer|nrp/i.test(x.attrs.price_type ?? '')) ?? priceNode[0];
    const etim = all(p, 'REFERENCE_FEATURE_GROUP_ID').map(txt).find((c) => ETIM[c]);
    const url = all(p, 'MIME_SOURCE').map(txt).find((s) => /^https?:\/\//i.test(s));
    return {
      pn: txt(first(p, 'SUPPLIER_PID')) || txt(first(p, 'SUPPLIER_AID')) || txt(first(p, 'MANUFACTURER_PID')) || txt(first(p, 'MANUFACTURER_AID')),
      maker: txt(first(p, 'MANUFACTURER_NAME')),
      d,
      type: f.SymbolTypes || (etim ? ETIM[etim] : undefined),
      price: price ? txt(first(price, 'PRICE_AMOUNT')) : undefined,
      cur: price ? txt(first(price, 'PRICE_CURRENCY')) || defCur : undefined,
      w: f.Width, h: f.Height, dp: f.Depth,
      url,
      f,
      acc,
    };
  }
}

/** "??_??@Text;de_DE@Schütz;en_US@Contactor;" → {de: 'Schütz', en: 'Contactor'} */
function eplanTexts(v: string): Record<string, string> {
  const out: Record<string, string> = {};
  if (!v.includes('@')) return v.trim() ? { tr: v.trim() } : out;
  for (const part of v.split(';')) {
    const at = part.indexOf('@');
    if (at < 0) continue;
    const l = langOf(part.slice(0, at)), t = part.slice(at + 1).trim();
    if (l && t && !out[l]) out[l] = t;
    else if (!l && t && !out.tr && !out.en) out.xx = t;
  }
  if (out.xx) {
    if (!Object.keys(out).some((k) => k !== 'xx')) out.tr = out.xx;
    delete out.xx;
  }
  return out;
}

function eplan(doc: XmlNode): string[][] {
  return all(doc, 'part').map(eplanRec).filter((r) => r.pn).map(toRow);
}

function eplanRec(p: XmlNode): Rec {
  {
    const a = p.attrs;
    const d = { ...eplanTexts(a.P_ARTICLE_DESCR1 ?? ''), };
    for (const [l, t] of Object.entries(eplanTexts(a.P_ARTICLE_DESCR2 ?? ''))) d[l] ??= t;
    const url = Object.values(a).find((v) => /^https?:\/\//i.test(v));
    return {
      pn: a.P_ARTICLE_PARTNR ?? a.P_ARTICLE_ORDERNR ?? '',
      maker: a.P_ARTICLE_MANUFACTURER_NAME || a.P_ARTICLE_MANUFACTURER || '',
      d,
      price: a.P_ARTICLE_SALESPRICE_1 || a.P_ARTICLE_PURCHASEPRICE_1 || '',
      cur: a.P_ARTICLE_CURRENCY ?? '',
      w: a.P_ARTICLE_WIDTH ?? '', h: a.P_ARTICLE_HEIGHT ?? '', dp: a.P_ARTICLE_DEPTH ?? '',
      url,
    };
  }
}

/** The element name that repeats most under one parent: the records of a plain XML list. */
function generic(doc: XmlNode): string[][] {
  let best: XmlNode[] = [];
  const visit = (n: XmlNode) => {
    const groups = new Map<string, XmlNode[]>();
    for (const c of n.children) groups.set(c.name, [...(groups.get(c.name) ?? []), c]);
    for (const g of groups.values()) if (g.length > best.length) best = g;
    n.children.forEach(visit);
  };
  visit(doc);
  if (best.length < 1) return [];
  const records = best.map((r) => {
    const f: Record<string, string> = { ...r.attrs };
    for (const c of r.children) if (!c.children.length && txt(c)) f[c.name] = txt(c);
    return f;
  });
  const head = [...new Set(records.flatMap((r) => Object.keys(r)))];
  return [head, ...records.map((r) => head.map((h) => r[h] ?? ''))];
}

/** Turns a parts XML file into import rows (first row: column names). */
export function partsXmlRows(src: string): XmlRows {
  const doc = parseXml(src.replace(/^﻿/, ''));
  if (doc.name === 'BMECAT' || all(doc, 'T_NEW_CATALOG').length || all(doc, 'T_UPDATE_PRODUCTS').length) return { format: 'bmecat', rows: [HEAD, ...bmecat(doc)] };
  if (all(doc, 'part').some((p) => p.attrs.P_ARTICLE_PARTNR)) return { format: 'eplan', rows: [HEAD, ...eplan(doc)] };
  return { format: 'generic', rows: generic(doc) };
}

/** Column names of the rows the BMEcat / EPLAN readers produce. */
export const PARTS_XML_HEAD = HEAD;

/**
 * Reads a large BMEcat or EPLAN parts XML piece by piece: push text as it
 * arrives, get the finished records back as import rows (without the header
 * row). Only one record is parsed at a time, so files far larger than memory
 * would allow as one tree can be imported.
 */
export class PartsXmlStream {
  format: 'bmecat' | 'eplan' | null = null;
  private buf = '';
  private ctx: BmecatCtx = { lang: 'de', cur: '' };
  private headerDone = false;

  push(text: string): string[][] {
    this.buf += text;
    return this.drain(false);
  }

  end(): string[][] {
    const rows = this.drain(true);
    if (!this.format) throw new Error('Büyük XML dosyaları yalnızca BMEcat ya da EPLAN parça biçiminde okunabilir.');
    return rows;
  }

  private drain(last: boolean): string[][] {
    if (!this.format) {
      if (/<(\w+:)?BMECAT[\s>]|T_NEW_CATALOG|T_UPDATE_PRODUCTS/.test(this.buf)) this.format = 'bmecat';
      else if (/P_ARTICLE_PARTNR/.test(this.buf)) this.format = 'eplan';
      else if (this.buf.length > 4_000_000 || last) return [];
      else return [];
    }
    const out: string[][] = [];
    if (this.format === 'bmecat') {
      if (!this.headerDone) {
        const e = this.buf.search(/<\/(\w+:)?HEADER>/);
        if (e < 0) return out;
        const b = this.buf.search(/<(\w+:)?HEADER[\s>]/);
        if (b >= 0 && b < e) {
          try {
            this.ctx = bmecatCtx(parseXml(this.buf.slice(b, this.buf.indexOf('>', e) + 1)));
          } catch {
            /* keep defaults */
          }
        }
        this.headerDone = true;
        this.buf = this.buf.slice(this.buf.indexOf('>', e) + 1);
      }
      const open = /<(\w+:)?(PRODUCT|ARTICLE)[\s>]/g;
      let cut = 0;
      for (;;) {
        open.lastIndex = cut;
        const m = open.exec(this.buf);
        if (!m) break;
        const close = new RegExp(`</(\w+:)?${m[2]}>`, 'g');
        close.lastIndex = m.index;
        const c = close.exec(this.buf);
        if (!c) break;
        const end = c.index + c[0].length;
        try {
          const rec = bmecatRec(parseXml(this.buf.slice(m.index, end)), this.ctx);
          if (rec.pn) out.push(toRow(rec));
        } catch {
          /* skip a broken record */
        }
        cut = end;
      }
      this.buf = this.buf.slice(cut);
    } else {
      let cut = 0;
      for (;;) {
        const b = this.buf.indexOf('<part', cut);
        if (b < 0 || b + 6 > this.buf.length) break;
        if (!/\s/.test(this.buf[b + 5])) {
          cut = b + 5;
          continue;
        }
        // end of the start tag, outside quoted attribute values
        let e = b + 5, q = '';
        for (; e < this.buf.length; e++) {
          const ch = this.buf[e];
          if (q) {
            if (ch === q) q = '';
          } else if (ch === '"' || ch === "'") q = ch;
          else if (ch === '>') break;
        }
        if (e >= this.buf.length) break;
        let end = e + 1;
        if (this.buf[e - 1] !== '/') {
          const c = this.buf.indexOf('</part>', e);
          if (c < 0) break;
          end = c + 7;
        }
        try {
          const rec = eplanRec(parseXml(this.buf.slice(b, end)));
          if (rec.pn) out.push(toRow(rec));
        } catch {
          /* skip a broken record */
        }
        cut = end;
      }
      this.buf = this.buf.slice(cut);
    }
    return out;
  }
}

/* ---------------- writing BMEcat ---------------- */

/** Interface languages → ISO 639-2 codes of BMEcat. */
const LANG_OUT: Record<string, string> = { tr: 'tur', en: 'eng', de: 'deu', es: 'spa', fr: 'fra', pt: 'por', ru: 'rus', ar: 'ara', zh: 'zho', hi: 'hin', ja: 'jpn' };
/** Longest supplier part number BMEcat 2005 allows (SUPPLIER_PID). */
export const BMECAT_PID_MAX = 32;

const xe = (s: string | number) =>
  String(s).replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const cut = (s: string, n: number) => (s.length > n ? s.slice(0, n - 1) + '…' : s);

export interface BmecatOptions {
  /** the company the catalogue comes from */
  supplier: string;
  catalogId?: string;
  catalogName?: string;
  version?: string;
  /** generation date, YYYY-MM-DD */
  date: string;
}

/** Start of a BMEcat 2005 catalogue (T_NEW_CATALOG); products follow, then BMECAT_TAIL. */
export function bmecatHead(o: BmecatOptions): string {
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<BMECAT version="2005" xmlns="http://www.bmecat.org/bmecat/2005">',
    '<HEADER>',
    '<GENERATOR_INFO>EnerCAD</GENERATOR_INFO>',
    '<CATALOG>',
    ...Object.values(LANG_OUT).map((l, i) => `<LANGUAGE${i ? '' : ' default="true"'}>${l}</LANGUAGE>`),
    `<CATALOG_ID>${xe((o.catalogId || 'ENERCAD').slice(0, 20))}</CATALOG_ID>`,
    `<CATALOG_VERSION>${xe((o.version || '1.0').slice(0, 7))}</CATALOG_VERSION>`,
    ...(o.catalogName ? [`<CATALOG_NAME>${xe(cut(o.catalogName, 100))}</CATALOG_NAME>`] : []),
    `<DATETIME type="generation_date"><DATE>${xe(o.date)}</DATE></DATETIME>`,
    '</CATALOG>',
    `<SUPPLIER><SUPPLIER_NAME>${xe(cut(o.supplier || 'EnerCAD', 50))}</SUPPLIER_NAME></SUPPLIER>`,
    '</HEADER>',
    '<T_NEW_CATALOG>',
    '',
  ].join('\n');
}

export const BMECAT_TAIL = '</T_NEW_CATALOG>\n</BMECAT>\n';

/**
 * One part as a BMEcat 2005 PRODUCT, in the element order of the schema: descriptions in every language the part
 * has; EnerCAD's fields (symbol types, contacts, poles, channels, sizes, weight, cable data, auxiliary contact
 * terminals) as features of the feature system "ENERCAD-1.0"; price, link and accessories in their own
 * elements. Null for a part number longer than BMEcat allows.
 */
export function bmecatProduct(p: Part): string | null {
  if (!p.pn || p.pn.length > BMECAT_PID_MAX) return null;
  const texts: [string, string][] = [['tr', p.d || p.pn], ...Object.entries(p.dl ?? {}).filter(([l, t]) => l !== 'tr' && LANG_OUT[l] && t)];
  const num = (n: number | undefined) => (n == null || !Number.isFinite(n) ? [] : [String(n)]);
  const list = (v: string | undefined) => (v ?? '').split(',').map((x) => x.trim()).filter(Boolean);
  const feature = (name: string, values: string[], unit = '') =>
    values.length ? `<FEATURE><FNAME>${name}</FNAME>${values.map((v) => `<FVALUE>${xe(cut(v, 60))}</FVALUE>`).join('')}${unit ? `<FUNIT>${unit}</FUNIT>` : ''}</FEATURE>` : '';
  const features = [
    feature('SymbolTypes', p.t ?? []),
    feature('NO', num(p.no)), feature('NC', num(p.nc)), feature('Poles', num(p.poles)), feature('Channels', num(p.ch)),
    feature('Width', num(p.w), 'MMT'), feature('Height', num(p.h), 'MMT'), feature('Depth', num(p.dp), 'MMT'), feature('Weight', num(p.kg), 'KGM'),
    feature('CableType', p.ct ? [p.ct] : []), feature('Cores', list(p.cores)), feature('CrossSection', p.cs ? [p.cs] : []),
    feature('Shield', p.shield == null ? [] : [p.shield ? 'true' : 'false']), feature('AuxContacts', list(p.terms)), feature('Visual', p.vis ? [p.vis] : []),
  ].join('');
  const pdf = !!p.url && /\.pdf($|[?#])/i.test(p.url);
  return [
    '<PRODUCT mode="new">',
    `<SUPPLIER_PID>${xe(p.pn)}</SUPPLIER_PID>`,
    '<PRODUCT_DETAILS>',
    ...texts.map(([l, t]) => `<DESCRIPTION_SHORT lang="${LANG_OUT[l]}">${xe(cut(t, 150))}</DESCRIPTION_SHORT>`),
    ...texts.filter(([, t]) => t.length > 150).map(([l, t]) => `<DESCRIPTION_LONG lang="${LANG_OUT[l]}">${xe(t)}</DESCRIPTION_LONG>`),
    ...(p.maker ? [`<MANUFACTURER_PID>${xe(p.pn)}</MANUFACTURER_PID>`, `<MANUFACTURER_NAME>${xe(p.maker.slice(0, 50))}</MANUFACTURER_NAME>`] : []),
    '</PRODUCT_DETAILS>',
    ...(features ? [`<PRODUCT_FEATURES><REFERENCE_FEATURE_SYSTEM_NAME>${FEATURE_SYSTEM}</REFERENCE_FEATURE_SYSTEM_NAME>${features}</PRODUCT_FEATURES>`] : []),
    '<PRODUCT_ORDER_DETAILS><ORDER_UNIT>C62</ORDER_UNIT><CONTENT_UNIT>C62</CONTENT_UNIT><NO_CU_PER_OU>1</NO_CU_PER_OU></PRODUCT_ORDER_DETAILS>',
    // a price is required: a part without one is "on request"
    p.price != null && Number.isFinite(p.price)
      ? `<PRODUCT_PRICE_DETAILS><PRODUCT_PRICE price_type="net_list"><PRICE_AMOUNT>${p.price}</PRICE_AMOUNT>${p.cur ? `<PRICE_CURRENCY>${xe(p.cur)}</PRICE_CURRENCY>` : ''}</PRODUCT_PRICE></PRODUCT_PRICE_DETAILS>`
      : '<PRODUCT_PRICE_DETAILS><PRODUCT_PRICE price_type="on_request"/></PRODUCT_PRICE_DETAILS>',
    ...(p.url && p.url.length <= 255
      ? [`<MIME_INFO><MIME><MIME_TYPE>${pdf ? 'application/pdf' : 'text/html'}</MIME_TYPE><MIME_SOURCE>${xe(p.url)}</MIME_SOURCE><MIME_PURPOSE>${pdf ? 'data_sheet' : 'others'}</MIME_PURPOSE></MIME></MIME_INFO>`]
      : []),
    // the schema counts accessories in whole pieces
    ...(p.acc ?? []).map((a) => `<PRODUCT_REFERENCE type="accessories" quantity="${Math.max(1, Math.round(a.n))}"><PROD_ID_TO>${xe(a.pn.slice(0, 80))}</PROD_ID_TO></PRODUCT_REFERENCE>`),
    '</PRODUCT>',
    '',
  ].join('\n');
}

/** A whole BMEcat 2005 catalogue; parts whose number is too long for BMEcat are left out and listed. */
export function partsToBmecat(parts: Part[], o: BmecatOptions): { xml: string; skipped: string[] } {
  const skipped: string[] = [];
  const body = parts.map((p) => bmecatProduct(p) ?? (skipped.push(p.pn), '')).join('');
  return { xml: bmecatHead(o) + body + BMECAT_TAIL, skipped };
}
